// 排版結果 → 獨立的 SVG 字串。文字轉為字形路徑（以 <use> 重複引用），不依賴系統字型。
import type { LayoutNode, MapLayout, Point } from './layout.js';
import { type FontSet, type FontKey, type TextBlock, placeGlyphs, glyphPathData, layoutText } from './text.js';
import { pen, fmt, taperedStroke } from './pen.js';
import { drawIcon } from './icons.js';
import { escapeXml } from './xml.js';
import { buildTimeline } from './animation.js';
import { Animator, type AnimateOptions } from './svg-animate.js';
import { hash32 } from './model.js';

export interface RenderOptions {
  /** 動態 SVG：建構動畫與流動光點（true 用預設值）。PNG 與一般 SVG 不要開 */
  animate?: boolean | AnimateOptions;
  /** 四周留白（像素） */
  padding?: number;
  /** false 時背景透明 */
  background?: boolean;
  /** 嵌入 SVG metadata 的大綱原文，供日後再匯入 */
  source?: string;
  /** 編輯器用：加上點擊區域與 data-relation／data-toggle 標記（匯出時不要開） */
  interactive?: boolean;
}

// SVG 畫布尺寸與內容位移；編輯器據此把排版座標換算成畫面座標。
export function svgFrame(layout: MapLayout, padding = 40): { width: number; height: number; offsetX: number; offsetY: number } {
  const { bounds } = layout;
  return {
    width: Math.ceil(bounds.w + padding * 2),
    height: Math.ceil(bounds.h + padding * 2),
    offsetX: padding - bounds.x,
    offsetY: padding - bounds.y,
  };
}

// 收集用到的字形，輸出為 <defs>，同一字形只定義一次。
class GlyphBook {
  private readonly used = new Map<string, string>();
  constructor(private readonly fonts: FontSet) {}

  // 把文字區塊畫成一串 <use>。
  text(block: TextBlock, left: number, top: number, color: string, align: 'left' | 'center' = 'center'): string {
    const glyphs = placeGlyphs(this.fonts, block, left, top, align);
    if (!glyphs.length) return '';
    const uses = glyphs.map(glyph => {
      const id = this.define(glyph.font, glyph.glyphId);
      return `<use href="#${id}" transform="translate(${fmt(glyph.x)} ${fmt(glyph.y)}) scale(${Math.round(glyph.scale * 1e5) / 1e5})"/>`;
    });
    return `<g fill="${color}">${uses.join('')}</g>`;
  }

  private define(font: FontKey, glyphId: number): string {
    const id = `g${font === 'latin' ? 'l' : 'c'}${glyphId}`;
    if (!this.used.has(id)) this.used.set(id, glyphPathData(this.fonts, font, glyphId));
    return id;
  }

  defs(): string {
    return [...this.used].sort(([a], [b]) => a.localeCompare(b)).map(([id, d]) => `<path id="${id}" d="${d}"/>`).join('');
  }
}

// 主要入口。
export function renderSvg(layout: MapLayout, fonts: FontSet, options: RenderOptions = {}): string {
  const { theme } = layout;
  const { width, height, offsetX, offsetY } = svgFrame(layout, options.padding ?? 40);
  const book = new GlyphBook(fonts);
  const body: string[] = [];

  // 動畫（選用）：時間軸決定每個元素的出現時間；不開時以下包裝都原樣輸出。
  const animateOptions: AnimateOptions | null = options.animate ? (options.animate === true ? {} : options.animate) : null;
  const timeline = animateOptions ? buildTimeline(layout, { maxDuration: animateOptions.maxDuration ?? 7 }) : null;
  const anim = animateOptions && timeline ? new Animator(`s${hash32(layout.nodes[0]?.id ?? '').toString(36)}`, timeline.end, animateOptions) : null;

  // 外框分組（最底層）
  for (const boundary of layout.boundaries) {
    let markup = pen.roundedRect(boundary.x, boundary.y, boundary.w, boundary.h, 14,
      { stroke: boundary.swatch.stroke, strokeWidth: 1.6, dash: '9 7', fill: boundary.swatch.fill, fillStyle: 'solid', fillOpacity: 0.35, roughness: 0.8 }, `b:${boundary.id}`);
    if (boundary.label) {
      const labelX = boundary.x + 16;
      const labelY = boundary.y - boundary.label.height / 2;
      markup += `<rect x="${fmt(labelX - 6)}" y="${fmt(labelY)}" width="${fmt(boundary.label.width + 12)}" height="${fmt(boundary.label.height)}" rx="4" fill="${theme.background}"/>`;
      markup += book.text(boundary.label, labelX, labelY, boundary.swatch.stroke);
    }
    body.push(anim ? anim.fade(markup, timeline!.boundaries.get(boundary.id)) : markup);
  }

  // 父子連線
  for (const edge of layout.edges) {
    const seed = `e:${edge.to}`;
    const span = timeline?.edges.get(edge.to);
    if (edge.kind === 'trunk') {
      const [a, b, c, d] = edge.points as [Point, Point, Point, Point];
      const centerline = `M${fmt(a.x)} ${fmt(a.y)}C${fmt(b.x)} ${fmt(b.y)} ${fmt(c.x)} ${fmt(c.y)} ${fmt(d.x)} ${fmt(d.y)}`;
      const markup = taperedStroke(edge.points, edge.width, edge.color, seed);
      body.push(anim ? anim.reveal(markup, centerline, edge.width, span) : markup);
      anim?.flow(centerline, edge.color, timeline!.depth.get(edge.to) ?? 1);
    } else if (edge.kind === 'elbow') {
      const markup = pen.polyline(edge.points, { stroke: edge.color, strokeWidth: edge.width, roughness: 0.8 }, seed);
      body.push(anim ? anim.draw(markup, span) : markup);
      anim?.flow(`M${edge.points.map(point => `${fmt(point.x)} ${fmt(point.y)}`).join('L')}`, edge.color, timeline!.depth.get(edge.to) ?? 1);
    } else {
      const [a, b, c, d] = edge.points as [Point, Point, Point, Point];
      const curve = `M${fmt(a.x)} ${fmt(a.y)}C${fmt(b.x)} ${fmt(b.y)} ${fmt(c.x)} ${fmt(c.y)} ${fmt(d.x)} ${fmt(d.y)}`;
      const markup = pen.path(curve, { stroke: edge.color, strokeWidth: edge.width, roughness: 0.9 }, seed);
      body.push(anim ? anim.draw(markup, span) : markup);
      anim?.flow(curve, edge.color, timeline!.depth.get(edge.to) ?? 1);
    }
  }

  // 節點
  for (const node of layout.nodes) {
    const markup = renderNode(node, layout, book, fonts, options.interactive ?? false);
    body.push(anim ? anim.pop(markup, timeline!.nodes.get(node.id)) : markup);
  }

  // 關聯線（最上層，避免被節點蓋住）
  layout.relations.forEach((relation, index) => {
    const [a, b, c, d] = relation.points;
    const seed = `r:${relation.from}>${relation.to}`;
    const style = { stroke: theme.relation, strokeWidth: 1.6, roughness: 0.9, ...(relation.style === 'solid' ? {} : { dash: '8 6' }) };
    const curve = `M${fmt(a.x)} ${fmt(a.y)}C${fmt(b.x)} ${fmt(b.y)} ${fmt(c.x)} ${fmt(c.y)} ${fmt(d.x)} ${fmt(d.y)}`;
    const parts: string[] = [];
    if (options.interactive) parts.push(`<g data-relation="${escapeXml(`${relation.from}>${relation.to}`)}"><path d="${curve}" fill="none" stroke="transparent" stroke-width="14"/>`);
    parts.push(pen.path(curve, style, seed));
    const angle = Math.atan2(d.y - c.y, d.x - c.x);
    const arrow = (offset: number): Point => ({ x: d.x - Math.cos(angle + offset) * 13, y: d.y - Math.sin(angle + offset) * 13 });
    parts.push(pen.polyline([arrow(0.45), d, arrow(-0.45)], { stroke: theme.relation, strokeWidth: 1.6, roughness: 0.6 }, seed + 'h'));
    if (relation.label) {
      const left = relation.labelAt.x - relation.label.width / 2;
      const top = relation.labelAt.y - relation.label.height / 2;
      parts.push(`<rect x="${fmt(left - 6)}" y="${fmt(top - 3)}" width="${fmt(relation.label.width + 12)}" height="${fmt(relation.label.height + 6)}" rx="6" fill="${theme.background}" fill-opacity="0.9"/>`);
      parts.push(book.text(relation.label, left, top, theme.relation));
    }
    if (options.interactive) parts.push('</g>');
    body.push(anim ? anim.fade(parts.join(''), timeline!.relations[index], true) : parts.join(''));
  });
  if (anim) body.push(anim.overlay());

  const title = escapeXml(layout.nodes[0]?.topic.title ?? 'Sind');
  const metadata = options.source !== undefined
    ? `<metadata><sind:source xmlns:sind="https://github.com/sind-mind/sind" version="1"><![CDATA[${options.source.replace(/]]>/g, ']]]]><![CDATA[>')}]]></sind:source></metadata>`
    : '';
  const background = options.background === false ? '' : `<rect width="100%" height="100%" fill="${theme.background}"/>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img">`
    + `<title>${title}</title>${metadata}${anim ? anim.style() : ''}<defs>${book.defs()}${anim ? anim.defs() : ''}</defs>${background}`
    + `<g transform="translate(${fmt(offsetX)} ${fmt(offsetY)})">${body.join('')}</g></svg>`;
}

// 單一節點：外形、圖示、文字、收合徽章；備註以 <title> 提示。
function renderNode(node: LayoutNode, layout: MapLayout, book: GlyphBook, fonts: FontSet, interactive: boolean): string {
  const { theme } = layout;
  const parts: string[] = [];
  const seed = `n:${node.id}`;
  // 互動模式：透明點擊區，底線樣式的節點文字間空隙也點得到。
  if (interactive) parts.push(`<rect x="${fmt(node.x)}" y="${fmt(node.y)}" width="${fmt(node.w)}" height="${fmt(node.h)}" fill="transparent"/>`);
  if (node.shape === 'root') {
    parts.push(pen.roundedRect(node.x, node.y, node.w, node.h, Math.min(18, node.h / 3),
      { stroke: node.swatch.stroke, strokeWidth: 2.6, fill: node.swatch.fill, fillStyle: 'solid' }, seed));
  } else if (node.shape === 'box') {
    const isBranch = node.depth === 1;
    const fill = isBranch && theme.branchFill !== 'none' ? { fill: node.swatch.fill, fillStyle: theme.branchFill } : { fill: theme.background, fillStyle: 'solid' as const };
    parts.push(pen.roundedRect(node.x, node.y, node.w, node.h, Math.min(12, node.h / 3),
      { stroke: node.swatch.stroke, strokeWidth: isBranch ? 2 : 1.5, ...fill }, seed));
  } else {
    parts.push(pen.line(node.x, node.y + node.h, node.x + node.w, node.y + node.h, { stroke: node.swatch.stroke, strokeWidth: node.depth === 2 ? 2 : 1.6, roughness: 0.8 }, seed));
  }
  if (node.icon) parts.push(drawIcon(node.icon.name, node.icon.x, node.icon.y, node.icon.size, node.swatch, `i:${node.id}`));
  const alertColor = theme.swatches.red?.stroke ?? '#e03131';
  parts.push(book.text(node.text, node.textX, node.textY, node.topic.alert ? alertColor : theme.text));
  if (node.progress) parts.push(renderProgress(node, layout, book));
  if (node.hidden) {
    const badge = renderBadge(node, layout, book, fonts);
    parts.push(interactive ? `<g data-toggle="${escapeXml(node.id)}">${badge}</g>` : badge);
  }
  const note = node.topic.note ? `<title>${escapeXml(node.topic.note)}</title>` : '';
  if (node.topic.note) parts.push(renderNoteMark(node));
  if (node.topic.alertHidden) parts.push(`<circle cx="${fmt(node.x + node.w)}" cy="${fmt(node.y)}" r="4.5" fill="${alertColor}"/>`);
  const muted = node.topic.muted ? ' opacity="0.45"' : '';
  return `<g data-id="${escapeXml(node.id)}"${muted}>${note}${parts.join('')}</g>`;
}

// 進度標籤：手繪圓角小框，框線與文字用分支色。
function renderProgress(node: LayoutNode, layout: MapLayout, book: GlyphBook): string {
  const { x, y, w, h, text } = node.progress!;
  return pen.roundedRect(x, y, w, h, h / 2, { stroke: node.swatch.stroke, strokeWidth: 1.2, fill: layout.theme.background, fillStyle: 'solid', roughness: 0.5 }, `p:${node.id}`)
    + book.text(text, x + (w - text.width) / 2, y + (h - text.height) / 2, node.swatch.stroke);
}

// 收合徽章：顯示隱藏的子孫數。
function renderBadge(node: LayoutNode, layout: MapLayout, book: GlyphBook, fonts: FontSet): string {
  const label = layoutText(fonts, String(node.hidden), 11, 100);
  const size = Math.max(18, label.width + 10);
  const anchorY = node.side === 0 ? node.y + node.h + 11 : node.shape === 'underline' ? node.y + node.h : node.y + node.h / 2;
  const cx = node.side === 0 ? node.x + node.w / 2 : node.side === 1 ? node.x + node.w + 4 + size / 2 : node.x - 4 - size / 2;
  return pen.ellipse(cx, anchorY, size, 18, { stroke: node.swatch.stroke, strokeWidth: 1.4, fill: layout.theme.background, fillStyle: 'solid', roughness: 0.6 }, `c:${node.id}`)
    + book.text(label, cx - label.width / 2, anchorY - label.height / 2, node.swatch.stroke);
}

// 有備註的節點，右上角畫一個小摺角記號。
function renderNoteMark(node: LayoutNode): string {
  const x = node.x + node.w - 3;
  const y = node.y + (node.shape === 'underline' ? 2 : 3);
  return `<path d="M${fmt(x - 9)} ${fmt(y)}L${fmt(x)} ${fmt(y)}L${fmt(x)} ${fmt(y + 9)}Z" fill="${node.swatch.stroke}" fill-opacity="0.7"/>`;
}
